/**
 * PDP: teleport loyalty naar pop-up na morph(main). Werkt zonder nieuwe app-CDN;
 * app-blok scripts draaien niet opnieuw na morph en oude CDN exporteert geen reinit.
 */
(function () {
  'use strict';

  if (window.__hdsLoyaltyTeleportBridgeBound) {
    return;
  }
  window.__hdsLoyaltyTeleportBridgeBound = true;

  /** @type {string | null} */
  let pendingTrigger = null;
  let reinitScheduled = false;

  /** @type {string | null} */
  let pendingPointsTrigger = null;
  let pointsRefreshScheduled = false;

  /** @type {AbortController | null} */
  let pointsFetchAbort = null;

  /**
   * @param {string} value
   * @returns {string}
   */
  function escapeAttrValue(value) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(value);
    }
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  /**
   * @param {Element} host
   * @param {string} selector
   * @returns {Element | null}
   */
  function queryWithinComponentHost(host, selector) {
    if (!(host instanceof Element)) return null;
    const inLight = host.querySelector(selector);
    if (inLight) return inLight;
    const sr = host.shadowRoot;
    return sr ? sr.querySelector(selector) : null;
  }

  /**
   * @param {HTMLElement} root
   * @param {string} key
   * @returns {HTMLElement | null}
   */
  function findMountForRoot(root, key) {
    const escaped = escapeAttrValue(key);
    const selector = `[data-loyalty-mount="${escaped}"]`;

    const row = root.closest('.group-block-content');
    if (row instanceof HTMLElement) {
      const inRow = row.querySelector(selector);
      if (inRow instanceof HTMLElement) return inRow;
    }

    const group = root.closest('.group-block');
    if (group instanceof HTMLElement) {
      const dialog = group.querySelector('dialog-component');
      if (dialog instanceof HTMLElement) {
        const inDialog = dialog.querySelector(selector);
        if (inDialog instanceof HTMLElement) return inDialog;
      }
    }

    const fallback = document.querySelector(selector);
    return fallback instanceof HTMLElement ? fallback : null;
  }

  /**
   * @param {HTMLElement} root
   */
  function restoreTeleportSourceVisibility(root) {
    let el = root.parentElement;
    for (let i = 0; i < 8 && el instanceof HTMLElement; i++) {
      if (el.classList.contains('shopify-app-block')) {
        el.removeAttribute('data-loyalty-teleport-emptied');
        el.removeAttribute('aria-hidden');
        el.style.removeProperty('display');
      }
      el = el.parentElement;
    }
  }

  /**
   * @param {HTMLElement | null} formerParent
   */
  function collapseEmptyTeleportSlot(formerParent) {
    if (!(formerParent instanceof HTMLElement)) return;

    const meaningfulText = function (el) {
      return (el.textContent || '').replace(/\u00a0/g, ' ').trim().length > 0;
    };

    const subtreeOnlyEmpty = function (el) {
      if (meaningfulText(el)) return false;
      if (el.childElementCount === 0) return true;
      return Array.from(el.children).every(function (c) {
        return c instanceof HTMLElement && subtreeOnlyEmpty(c);
      });
    };

    let el = formerParent;
    for (let i = 0; i < 8 && el instanceof HTMLElement; i++) {
      if (el.classList.contains('shopify-app-block') && subtreeOnlyEmpty(el)) {
        el.setAttribute('data-loyalty-teleport-emptied', '');
        el.setAttribute('aria-hidden', 'true');
        el.style.display = 'none';
        return;
      }
      el = el.parentElement;
    }
  }

  /**
   * @param {string} raw
   * @returns {string}
   */
  function normalizeShopifyNumericId(raw) {
    if (!raw) return '';
    const s = String(raw).trim();
    const m = s.match(/(\d+)$/);
    return m ? m[1] : s;
  }

  /**
   * Live variant/product uit DOM na morph (picker + cart form).
   * @param {HTMLElement} widget
   */
  function bridgeSyncWidgetDatasetFromPage(widget) {
    const picker = document.querySelector(
      'variant-picker[data-template-product-match="true"][data-product-id], variant-picker[data-product-id]'
    );
    if (picker instanceof HTMLElement && picker.dataset.productId) {
      widget.setAttribute(
        'data-product-id',
        normalizeShopifyNumericId(picker.dataset.productId)
      );
    }

    const variantInput = document.querySelector(
      'product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]'
    );
    if (variantInput instanceof HTMLInputElement && variantInput.value) {
      widget.setAttribute(
        'data-variant-id',
        normalizeShopifyNumericId(variantInput.value)
      );
    }
  }

  /**
   * @param {HTMLElement} widget
   * @param {boolean} loading
   */
  function bridgeSetWidgetValueLoading(widget, loading) {
    const valueEl = widget.querySelector('.loyalty-points-value');
    if (!(valueEl instanceof HTMLElement)) return;
    if (loading) {
      valueEl.classList.add('loyalty-points-value--loading');
      valueEl.setAttribute('aria-busy', 'true');
      valueEl.innerHTML =
        '<span class="loyalty-points__skeleton-line" aria-hidden="true"></span>';
    } else {
      valueEl.classList.remove('loyalty-points-value--loading');
      valueEl.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {HTMLElement} widget
   * @param {number | null | undefined} points
   */
  function bridgeSetWidgetPoints(widget, points) {
    const valueEl = widget.querySelector('.loyalty-points-value');
    if (!(valueEl instanceof HTMLElement)) return;
    bridgeSetWidgetValueLoading(widget, false);
    if (points === null || points === undefined) {
      valueEl.textContent = '—';
    } else {
      valueEl.textContent = Number(points).toLocaleString('nl-NL');
    }
    syncPopupTriggerFromWidget(widget);
  }

  /**
   * @param {HTMLElement} widget
   * @returns {boolean}
   */
  function bridgeTryActivateAppWidget(widget) {
    const tag = customElements.get('loyalty-points-widget');
    if (!tag || !(widget instanceof tag)) return false;

    bridgeSyncWidgetDatasetFromPage(widget);

    if (typeof widget.fetchPointsImmediate === 'function') {
      widget.fetchPointsImmediate();
      return true;
    }
    if (typeof widget.fetchPoints === 'function') {
      widget.fetchPoints();
      return true;
    }
    return false;
  }

  /**
   * @param {HTMLElement} widget
   * @param {AbortSignal} signal
   * @returns {Promise<void>}
   */
  async function bridgeFetchPointsPlain(widget, signal) {
    bridgeSyncWidgetDatasetFromPage(widget);

    const variantId = widget.getAttribute('data-variant-id');
    const priceCents = widget.getAttribute('data-price-cents');
    const currency = widget.getAttribute('data-currency') || 'EUR';
    const customerEmail = widget.getAttribute('data-customer-email') || '';
    const productId = widget.getAttribute('data-product-id');
    const collectionIds = widget.getAttribute('data-collection-ids');
    const quantity = widget.getAttribute('data-quantity') || '1';

    if (!variantId || !priceCents) {
      bridgeSetWidgetPoints(widget, null);
      return;
    }

    const url = new URL('/apps/loyalty-points', window.location.origin);
    url.searchParams.set('variant_id', variantId);
    url.searchParams.set('price_cents', priceCents);
    url.searchParams.set('currency', currency);
    if (customerEmail) {
      url.searchParams.set('customer_email', customerEmail);
    }
    if (productId) {
      url.searchParams.set('product_id', productId);
    }
    if (collectionIds) {
      url.searchParams.set('collection_ids', collectionIds);
    }
    url.searchParams.set('quantity', quantity);

    bridgeSetWidgetValueLoading(widget, true);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        signal,
      });

      if (!response.ok) {
        throw new Error('HTTP ' + response.status);
      }

      const data = await response.json();
      if (data && data.points !== null && data.points !== undefined) {
        bridgeSetWidgetPoints(widget, data.points);
      } else {
        bridgeSetWidgetPoints(widget, null);
      }
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      bridgeSetWidgetPoints(widget, null);
    }
  }

  /**
   * @param {string} trigger
   */
  async function bridgeRefreshAllWidgetPoints() {
    const widgets = Array.from(
      document.querySelectorAll('[data-loyalty-widget]')
    ).filter(function (el) {
      return el instanceof HTMLElement;
    });

    if (!widgets.length) return;

    if (pointsFetchAbort) {
      pointsFetchAbort.abort();
    }
    pointsFetchAbort = new AbortController();
    const signal = pointsFetchAbort.signal;

    for (let i = 0; i < widgets.length; i++) {
      if (signal.aborted) break;
      const widget = widgets[i];
      if (bridgeTryActivateAppWidget(widget)) {
        continue;
      }
      await bridgeFetchPointsPlain(widget, signal);
    }

    if (!signal.aborted) {
      syncAllPopupTriggers();
    }
  }

  /**
   * @param {string} trigger
   */
  function schedulePointsRefresh(trigger) {
    pendingPointsTrigger = pendingPointsTrigger
      ? pendingPointsTrigger + '+' + trigger
      : trigger;
    if (pointsRefreshScheduled) return;
    pointsRefreshScheduled = true;

    queueMicrotask(function () {
      requestAnimationFrame(function () {
        pointsRefreshScheduled = false;
        const t = pendingPointsTrigger || trigger;
        pendingPointsTrigger = null;
        bridgeRefreshAllWidgetPoints(t);
      });
    });
  }

  /**
   * @param {HTMLElement} widget
   */
  function syncPopupTriggerFromWidget(widget) {
    const root = widget.closest('.loyalty-points-teleport-root');
    const mount = widget.closest('[data-loyalty-mount]');
    if (!root || !mount) return;

    const dialogHost = mount.closest('dialog-component');
    if (!(dialogHost instanceof HTMLElement)) return;

    const btn = queryWithinComponentHost(
      dialogHost,
      'button.popup-link__button'
    );
    const h2 = queryWithinComponentHost(dialogHost, '#popup-dialog-heading');
    if (!(btn instanceof HTMLElement)) return;

    const valEl = widget.querySelector('.loyalty-points-value');
    const beforeEl = widget.querySelector('.loyalty-points-text-before');
    const afterEl = widget.querySelector('.loyalty-points-text-after');

    const before =
      (beforeEl && beforeEl.textContent.trim()) ||
      widget.getAttribute('data-text-before') ||
      '';
    let val = (valEl && valEl.textContent.trim()) || '';
    if (
      valEl &&
      valEl.classList.contains('loyalty-points-value--loading')
    ) {
      val = '';
    }
    const after =
      (afterEl && afterEl.textContent.trim()) ||
      widget.getAttribute('data-text-after') ||
      '';

    const parts = [before, val, after].filter(function (p) {
      return p && p.length > 0;
    });
    const line = parts.join(' ').replace(/\s+/g, ' ').trim();
    if (!line) return;

    let slot = btn.querySelector('[data-loyalty-popup-label]');
    if (!slot) {
      slot = document.createElement('span');
      slot.setAttribute('data-loyalty-popup-label', '');
      const icon =
        btn.querySelector(':scope > .svg-wrapper') ||
        btn.querySelector('.svg-wrapper');
      if (icon && icon.parentNode === btn) {
        btn.insertBefore(slot, icon);
      } else {
        btn.insertBefore(slot, btn.firstChild);
      }
    }
    slot.textContent = line;
    if (h2) {
      h2.textContent = line;
    }
  }

  function syncAllPopupTriggers() {
    document
      .querySelectorAll(
        '.loyalty-points-teleport-root [data-loyalty-widget]'
      )
      .forEach(function (el) {
        if (el instanceof HTMLElement) {
          syncPopupTriggerFromWidget(el);
        }
      });
  }

  /**
   * Theme-eigen teleport (zelfde gedrag als app loyalty-points.js).
   */
  function bridgeTeleportRunAll() {
    const roots = Array.from(
      document.querySelectorAll(
        '.loyalty-points-teleport-root[data-loyalty-teleport-key]'
      )
    );

    /** @type {Map<HTMLElement, HTMLElement[]>} */
    const rootsByMount = new Map();

    roots.forEach(function (root) {
      if (!(root instanceof HTMLElement)) return;
      const key = (root.getAttribute('data-loyalty-teleport-key') || '').trim();
      if (!key || !/^[a-zA-Z0-9_-]+$/.test(key)) return;
      const mount = findMountForRoot(root, key);
      if (!(mount instanceof HTMLElement)) return;
      const list = rootsByMount.get(mount) || [];
      list.push(root);
      rootsByMount.set(mount, list);
    });

    rootsByMount.forEach(function (mountRoots, mount) {
      const key = (
        mountRoots[0].getAttribute('data-loyalty-teleport-key') || ''
      ).trim();
      if (!key) return;

      const escaped = escapeAttrValue(key);
      const outside = mountRoots.filter(function (r) {
        return !mount.contains(r);
      });
      const inside = mountRoots.filter(function (r) {
        return mount.contains(r);
      });

      const source =
        outside.length > 0 ? outside[outside.length - 1] : inside[0] || null;
      if (!source) return;

      restoreTeleportSourceVisibility(source);

      mount
        .querySelectorAll(
          '.loyalty-points-teleport-root[data-loyalty-teleport-key="' +
            escaped +
            '"]'
        )
        .forEach(function (stale) {
          if (stale !== source) stale.remove();
        });

      if (!mount.contains(source)) {
        const formerParent = source.parentElement;
        mount.appendChild(source);
        collapseEmptyTeleportSlot(formerParent);
      }

      const row = mount.closest('.group-block-content');
      if (row instanceof HTMLElement) {
        row.querySelectorAll('.shopify-app-block').forEach(function (block) {
          if (!(block instanceof HTMLElement)) return;
          if (!block.querySelector('.loyalty-points-teleport-root')) {
            collapseEmptyTeleportSlot(block);
          }
        });
      }
    });

    queueMicrotask(syncAllPopupTriggers);
  }

  /**
   * @param {string} trigger
   */
  function bridgeReinitializeDom(trigger) {
    bridgeTeleportRunAll();
    schedulePointsRefresh(trigger + '→after-teleport');
  }

  /**
   * @param {string} trigger
   */
  function scheduleReinitialize(trigger) {
    pendingTrigger = pendingTrigger ? pendingTrigger + '+' + trigger : trigger;
    if (reinitScheduled) return;
    reinitScheduled = true;

    queueMicrotask(function () {
      requestAnimationFrame(function () {
        reinitScheduled = false;
        const t = pendingTrigger || 'unknown';
        pendingTrigger = null;

        if (typeof window.__hdsLoyaltyReinitializeDom === 'function') {
          if (typeof window.__hdsLoyaltyScheduleReinitializeDom === 'function') {
            window.__hdsLoyaltyScheduleReinitializeDom(t);
          } else {
            window.__hdsLoyaltyReinitializeDom(t);
          }
          schedulePointsRefresh(t + '→app-delegated');
          return;
        }

        bridgeReinitializeDom(t);
      });
    });
  }

  /**
   * @param {string} trigger
   */
  function ensureLoyaltyThenReinit(trigger) {
    const hasAppReinit = typeof window.__hdsLoyaltyReinitializeDom === 'function';
    const runtimeBound = !!window.__hdsLoyaltyPointsRuntimeBound;

    if (hasAppReinit) {
      scheduleReinitialize(trigger);
      return;
    }

    if (runtimeBound) {
      scheduleReinitialize(trigger + '→bridge');
      return;
    }

    const src = (function () {
      const scripts = document.querySelectorAll(
        'script[src*="loyalty-points.js"]'
      );
      for (let i = scripts.length - 1; i >= 0; i--) {
        const s = scripts[i].getAttribute('src');
        if (s) return s;
      }
      return null;
    })();

    if (!src) {
      scheduleReinitialize(trigger + '→bridge-only');
      return;
    }

    if (!window.__hdsLoyaltyPointsLoadPromise) {
      window.__hdsLoyaltyPointsLoadPromise = new Promise(function (
        resolve,
        reject
      ) {
        const el = document.createElement('script');
        el.src = src;
        el.defer = true;
        el.onload = function () {
          resolve();
        };
        el.onerror = function () {
          reject(new Error('loyalty-script-load-failed'));
        };
        document.head.appendChild(el);
      });
    }

    window.__hdsLoyaltyPointsLoadPromise
      .then(function () {
        if (typeof window.__hdsLoyaltyReinitializeDom === 'function') {
          scheduleReinitialize(trigger + '→after-app-load');
        } else {
          scheduleReinitialize(trigger + '→bridge-after-load');
        }
      })
      .catch(function () {
        scheduleReinitialize(trigger + '→bridge-load-failed');
      });
  }

  window.__hdsLoyaltyReinitializeDomBridge = bridgeReinitializeDom;
  window.__hdsLoyaltyAfterMainMorph = function () {
    ensureLoyaltyThenReinit('variant-picker→bridge');
  };

  document.addEventListener('hds:loyalty-dom-reset', function () {
    ensureLoyaltyThenReinit('hds:loyalty-dom-reset→bridge');
  });

  document.addEventListener('variant:update', function (event) {
    const detail = event && event.detail;
    const data = detail && detail.data;
    const hasHtmlDoc = data && data.html instanceof Document;
    const hasNewProduct = data && data.newProduct != null;
    if (hasHtmlDoc || hasNewProduct) {
      ensureLoyaltyThenReinit('variant:update→bridge');
      return;
    }
    schedulePointsRefresh('variant:update→points-only');
  });

  if (document.readyState !== 'loading') {
    scheduleReinitialize('bridge-already-interactive');
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      scheduleReinitialize('bridge-DOMContentLoaded');
    });
  }
})();
